import { useEffect } from 'react';
import { GET_PLAYER_MONEY, GOT_PLAYER_MONEY } from 'constants/eventConstants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useGuest } from 'hooks/useGuest';
import { updateCurrentPlayerBankAmount } from 'reducers/currentTable';
import { updateBankAmount } from 'reducers/user';
import { decompressData } from 'utils/functions';

type PlayerMoneyPayload = {
  response: {
    playWinpot?: string | number | null;
    playChargeBalance?: string | number | null;
    playerBalance?: string | number | null;
    [key: string]: unknown;
  };
  promoNotifications?: unknown;
};

const hasMoneyFields = (value: any) =>
  value &&
  (value.playWinpot !== undefined ||
    value.playChargeBalance !== undefined ||
    value.playerBalance !== undefined);

export const normalizePlayerMoneyPayload = (data: unknown): PlayerMoneyPayload | null => {
  const parsed = decompressData(data);

  if (!parsed || typeof parsed !== 'object') return null;
  if ((parsed as any).success === false) return null;

  const raw = (parsed as any).data ?? parsed;
  if (!raw || typeof raw !== 'object') return null;
  if ((raw as any).error) return null;

  if ((raw as any).response) {
    const response = (raw as any).response;
    if (!hasMoneyFields(response)) return null;
    return raw as PlayerMoneyPayload;
  }

  if (!hasMoneyFields(raw)) return null;

  return {
    response: raw as PlayerMoneyPayload['response'],
    promoNotifications: (parsed as any).promoNotifications
  };
};

const usePlayerMoneySocketSync = (socket: any) => {
  const dispatch = useAppDispatch();
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const guest = useGuest();

  useEffect(() => {
    if (!socket) return;

    const handlePlayerMoney = (data: unknown) => {
      const payload = normalizePlayerMoneyPayload(data);
      if (!payload) return;

      dispatch(updateBankAmount(payload));
      dispatch(updateCurrentPlayerBankAmount(payload));
    };

    socket.on(GOT_PLAYER_MONEY, handlePlayerMoney);

    return () => {
      socket.off(GOT_PLAYER_MONEY, handlePlayerMoney);
    };
  }, [dispatch, socket]);

  useEffect(() => {
    if (!socket) return;
    // Guests have no balance — don't fetch player money (a stale playerGUID in
    // localStorage from a prior session would otherwise trigger this).
    if (guest) return;

    const guid = playerGUID || localStorage.getItem('playerGUID') || undefined;
    if (!guid) return;

    socket.emit(GET_PLAYER_MONEY, { playerGUID: guid });
  }, [guest, playerGUID, socket]);
};

export default usePlayerMoneySocketSync;
